/* Importing a broker's own record of what you did.
 *
 * §1 rules out connecting to a brokerage — no passwords, no trading keys, no
 * OAuth — which rules out polling an account and does not rule out this. The
 * broker already writes down every fill and will hand you the file.
 *
 * The mapping is confirmed rather than assumed. "Type" is the side at one
 * broker and the instrument class at another, and a column read wrong produces
 * a book that looks fine and is not. So the guess is a starting point, the
 * preview says exactly what will be written, and anything unreadable is listed
 * by line instead of being rounded in. */

import { useCallback, useMemo, useRef, useState } from 'react';
import { parseDelimited } from '../lib/csv';
import { guessColumns, readTrades, type ColumnMap, type ImportResult } from '../state/importTrades';
import { useStore } from '../state/store';
import { dateTime } from '../lib/format';
import type { AccountRecord, TradeColumns } from '../types';

const FIELDS: Array<{ key: keyof ColumnMap; label: string }> = [
  { key: 'at', label: 'Date' },
  { key: 'ticker', label: 'Ticker' },
  { key: 'side', label: 'Buy or sell' },
  { key: 'shares', label: 'Shares' },
  { key: 'price', label: 'Price' },
];

function complete(map: Partial<ColumnMap>): map is ColumnMap {
  return FIELDS.every((f) => typeof map[f.key] === 'number');
}

export function ImportTrades({ account, say }: {
  account: AccountRecord;
  say: (message: string) => void;
}) {
  const { state, runImport, undoImport } = useStore();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [rows, setRows] = useState<string[][] | null>(null);
  const [fileName, setFileName] = useState('');
  const [map, setMap] = useState<Partial<ColumnMap>>({});
  const [busy, setBusy] = useState(false);

  const header = rows?.[0] ?? [];

  const pick = useCallback(async (file: File) => {
    const text = await file.text();
    const parsed = parseDelimited(text);
    if (parsed.length < 2) {
      say('That file has a header and nothing under it.');
      return;
    }
    setRows(parsed);
    setFileName(file.name);
    // A mapping confirmed once for this broker is reused; otherwise, a guess.
    setMap(account.tradeColumns ?? guessColumns(parsed[0]!));
  }, [account.tradeColumns, say]);

  const result: ImportResult | null = useMemo(() => {
    if (!rows || !complete(map)) return null;
    return readTrades(rows, map, account.id, state.data.trades);
  }, [rows, map, account.id, state.data.trades]);

  const fresh = result?.rows.filter((r) => !r.duplicate) ?? [];
  const already = (result?.rows.length ?? 0) - fresh.length;

  const reset = () => { setRows(null); setMap({}); setFileName(''); };

  const run = async () => {
    if (!result || !complete(map)) return;
    setBusy(true);
    try {
      const done = await runImport({
        account: account.id,
        fileName,
        trades: fresh.map((r) => r.trade),
        columns: map as TradeColumns,
      });
      const parts = [`${done.recorded} recorded from ${fileName}`];
      if (done.opened) parts.push(`${done.opened} position${done.opened === 1 ? '' : 's'} opened`);
      if (done.closed) parts.push(`${done.closed} closed`);
      if (already) parts.push(`${already} were already in the book`);
      say(`${parts.join(', ')}.`);
      for (const problem of done.problems.slice(0, 3)) {
        say(`${problem.ticker}: ${problem.why}`);
      }
      reset();
    } finally {
      setBusy(false);
    }
  };

  const batches = state.data.importBatches.filter((b) => b.account === account.id);

  return (
    <div style={{ marginTop: 8 }}>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';                  // so the same file can be picked twice
          if (file) void pick(file);
        }}
      />

      {!rows ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>
            Import trades from a file
          </button>
          <span className="dim" style={{ fontSize: 11 }}>CSV or TSV</span>
        </div>
      ) : (
        <div className="card card-pad" style={{ marginTop: 6 }}>
          <div className="dim" style={{ fontSize: 11.5, marginBottom: 10, lineHeight: 1.45 }}>
            <strong>{fileName}</strong> · {rows.length - 1} rows. Check which column is which; the
            guess below is only a guess.
          </div>

          {FIELDS.map((f) => (
            <div className="field" key={f.key}>
              <label htmlFor={`map-${account.id}-${f.key}`}>{f.label}</label>
              <select
                id={`map-${account.id}-${f.key}`}
                className="select"
                value={map[f.key] ?? ''}
                onChange={(e) => setMap((m) => ({
                  ...m,
                  [f.key]: e.target.value === '' ? undefined : Number(e.target.value),
                }))}
              >
                <option value="">Pick a column</option>
                {header.map((h, i) => (
                  <option key={i} value={i}>{h.trim() || `column ${i + 1}`}</option>
                ))}
              </select>
            </div>
          ))}

          {result ? (
            <div style={{ marginTop: 12 }}>
              <div className="kv">
                <span className="k">To record</span>
                <span className="v num">{fresh.length}</span>
              </div>
              {already > 0 ? (
                <div className="kv">
                  <span className="k">Already in the book</span>
                  <span className="v num">{already}</span>
                </div>
              ) : null}
              {result.skipped.length > 0 ? (
                <div className="kv">
                  <span className="k warn">Unreadable rows</span>
                  <span className="v num warn">{result.skipped.length}</span>
                </div>
              ) : null}

              {result.dateOrder === 'mixed' ? (
                <div className="warn" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
                  The dates in this file contradict each other. Some read as day first, some as
                  month first. Nothing here can be trusted to land on the right day. Export it again
                  with ISO dates if the broker offers them.
                </div>
              ) : result.dateOrder !== 'iso' ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
                  Dates read as {result.dateOrder === 'day-first' ? 'day/month/year' : 'month/day/year'},
                  which is what the file itself indicates. Check one row against your broker before
                  trusting the rest.
                </div>
              ) : null}

              {result.skipped.length > 0 ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                  {result.skipped.slice(0, 4).map((s) => (
                    <div key={s.line}>line {s.line}: {s.why}</div>
                  ))}
                  {result.skipped.length > 4 ? <div>…and {result.skipped.length - 4} more</div> : null}
                </div>
              ) : null}

              <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
                This records the trades <strong>and moves the positions to match</strong>. A buy of
                a name you do not hold opens it, a sale that empties one closes it. Careful with a
                full history on top of positions that are already right: it would count every share
                twice. The run is kept as one piece and can be undone below.
              </div>
            </div>
          ) : (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              Pick a column for each field to see what would be recorded.
            </div>
          )}

          <div className="fab-row" style={{ marginTop: 12 }}>
            <button className="btn btn-block" onClick={reset}>Cancel</button>
            <button
              className="btn btn-primary btn-block"
              disabled={busy || fresh.length === 0 || result?.dateOrder === 'mixed'}
              onClick={() => void run()}
            >
              {busy ? 'Recording…' : `Record ${fresh.length}`}
            </button>
          </div>
        </div>
      )}

      {batches.length > 0 ? (
        <div style={{ marginTop: 10 }}>
          {batches.map((b) => (
            <div className="kv" key={b.id} style={{ marginBottom: 6 }}>
              <span className="k" style={{ fontSize: 11.5 }}>
                {b.fileName} · {b.tradeIds.length} trade{b.tradeIds.length === 1 ? '' : 's'}
              </span>
              <span className="dim num" style={{ fontSize: 10.5, marginLeft: 'auto' }}>
                {dateTime(b.at)}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { void undoImport(b.id); say(`${b.fileName} undone.`); }}
              >
                Undo
              </button>
            </div>
          ))}
          <div className="dim" style={{ fontSize: 10.5, marginTop: 4, lineHeight: 1.45 }}>
            Undoing puts the affected positions back exactly as they stood before that run, which
            also drops any change you made to them by hand since.
          </div>
        </div>
      ) : null}
    </div>
  );
}
